import { supabase } from '@/composables/supabaseClient'
import type { Tables } from '@/types/database.types'
import { defineStore } from 'pinia'
import { ref } from 'vue'

/**Commonly used estimate settings to prevent constant refetching of the same data */
export const useDatabaseStore = defineStore('database', () => {
  const unitsOfMeasure = ref<Tables<'unit_of_measure'>[]>()
  const laborTypes = ref<Tables<'labor_type'>[]>()
  const laborRates = ref<Tables<'labor_rate'>[]>()
  const equipmentTypes = ref<Tables<'equipment_type'>[]>()
  const equipmentRates = ref<Tables<'equipment_rate'>[]>()
  const costSources = ref<Tables<'cost_source'>[]>()
  const itemDivisions = ref<Tables<'item_division'>[]>()
  const items = ref<Tables<'item'>[]>()
  const itemCosts = ref<Tables<'item_cost'>[]>()
  const assemblies = ref<Tables<'assembly'>[]>()
  const assembyDivisions = ref<Tables<'assembly_division'>[]>()
  const assemblyItems = ref<Tables<'assembly_item'>[]>()
  const estimateTypes = ref<Tables<'estimate_type'>[]>()
  const estimateStatus = ref<Tables<'estimate_status'>[]>()
  const estimates = ref<Tables<'estimate'>[]>()

  async function initialize() {
    const loaders = []
    if (!unitsOfMeasure.value) {
      // Load uom data if it's not in memory
      loaders.push(
        supabase
          .from('unit_of_measure')
          .select()
          .then(({ data, error }) => {
            if (error) {
              throw new Error('unable to fetch units_of_measure')
            }
            unitsOfMeasure.value = data
          }),
      )
    }

    if (!laborTypes.value) {
      loaders.push(
        supabase
          .from('labor_type')
          .select()
          .then(({ data, error }) => {
            if (error) {
              throw new Error('unable to fetch labor_type')
            }
            laborTypes.value = data
          }),
      )
    }

    if (!laborRates.value) {
      loaders.push(
        supabase
          .from('labor_rate')
          .select()
          .then(({ data, error }) => {
            if (error) {
              throw new Error('unable to fetch labor_rate')
            }
            laborRates.value = data
          }),
      )
    }

    if (!equipmentTypes.value) {
      loaders.push(
        supabase
          .from('equipment_type')
          .select()
          .then(({ data, error }) => {
            if (error) {
              throw new Error('unable to fetch equipment_type')
            }
            equipmentTypes.value = data
          }),
      )
    }

    if (!equipmentRates.value) {
      loaders.push(
        supabase
          .from('equipment_rate')
          .select()
          .then(({ data, error }) => {
            if (error) {
              throw new Error('unable to fetch equipment_rate')
            }
            equipmentRates.value = data
          }),
      )
    }

    if (!costSources.value) {
      loaders.push(
        supabase
          .from('cost_source')
          .select()
          .then(({ data, error }) => {
            if (error) {
              throw new Error('unable to fetch cost_source')
            }
            costSources.value = data
          }),
      )
    }

    if (!itemDivisions.value) {
      loaders.push(
        supabase
          .from('item_division')
          .select()
          .then(({ data, error }) => {
            if (error) {
              throw new Error('unable to fetch item_division')
            }
            itemDivisions.value = data
          }),
      )
    }

    if (!items.value) {
      loaders.push(
        supabase
          .from('item')
          .select()
          .then(({ data, error }) => {
            if (error) {
              throw new Error('unable to fetch item')
            }
            items.value = data
          }),
      )
    }

    if (!itemCosts.value) {
      loaders.push(
        supabase
          .from('item_cost')
          .select()
          .then(({ data, error }) => {
            if (error) {
              throw new Error('unable to fetch item_cost')
            }
            itemCosts.value = data
          }),
      )
    }

    if (!assemblies.value) {
      loaders.push(
        supabase
          .from('assembly')
          .select()
          .then(({ data, error }) => {
            if (error) {
              throw new Error('unable to fetch assembly')
            }
            assemblies.value = data
          }),
      )
    }

    if (!assembyDivisions.value) {
      loaders.push(
        supabase
          .from('assembly_division')
          .select()
          .then(({ data, error }) => {
            if (error) {
              throw new Error('unable to fetch assembly_division')
            }
            assembyDivisions.value = data
          }),
      )
    }

    if (!assemblyItems.value) {
      loaders.push(
        supabase
          .from('assembly_item')
          .select()
          .then(({ data, error }) => {
            if (error) {
              throw new Error('unable to fetch assembly_item')
            }
            assemblyItems.value = data
          }),
      )
    }

    if (!estimateTypes.value) {
      loaders.push(
        supabase
          .from('estimate_type')
          .select()
          .then(({ data, error }) => {
            if (error) {
              throw new Error('unable to fetch estimate_type')
            }
            estimateTypes.value = data
          }),
      )
    }

    if (!estimateStatus.value) {
      loaders.push(
        supabase
          .from('estimate_status')
          .select()
          .then(({ data, error }) => {
            if (error) {
              throw new Error('unable to fetch estimate_status')
            }
            estimateStatus.value = data
          }),
      )
    }

    if (!estimates.value) {
      loaders.push(
        supabase
          .from('estimate')
          .select()
          .then(({ data, error }) => {
            if (error) {
              throw new Error('unable to fetch estimate')
            }
            estimates.value = data
          }),
      )
    }

    await Promise.all(loaders)
  }

  initialize()
  return {
    unitsOfMeasure,
    laborTypes,
    laborRates,
    equipmentTypes,
    equipmentRates,
    costSources,
    itemDivisions,
    items,
    itemCosts,
    assemblies,
    assemblyItems,
    assembyDivisions,
    estimateStatus,
    estimateTypes,
    estimates,
  }
})
